import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { Button, Input } from "@jhool-io/fe-components";
import BackLink from "../../../../components/BackLink/BackLink";
import ListState from "../../../../components/ListState/ListState";

import { formatDate, removeEnumUnderscore } from "../../../../utils/helpers";
import { useFetchUserDetails } from "../../../auth/hooks/auth.queries";

export default function WaitingRoom() {
    const [isEditing, setIsEditing] = useState(false);
    const { sessionHistoryId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const inputRef = useRef<HTMLInputElement | null>(null);

    const loggedInUser = useFetchUserDetails();

    const displayNameFromUrl = searchParams.get("display_name") || "";

    const navigate = useNavigate();

    const providerName = searchParams.get("with")?.split("_").join(" ");
    const startDateTime = searchParams.get("start_date_time") || "";
    const service = searchParams.get("service") || "";

    const onSaveClick = () => {
        if (!inputRef?.current?.value?.trim()) {
            return;
        }

        if (inputRef?.current && inputRef?.current?.value.trim()) {
            Sentry.logger.info("Client updated display name", {
                displayName: inputRef?.current?.value || "",
                previousDisplayName: displayNameFromUrl || "N/A",
                userId: loggedInUser?.data?.data?.user_id || "",
            });

            searchParams.set("display_name", inputRef?.current?.value || "");

            setIsEditing(false);
            setSearchParams(searchParams);
        }
    };

    return (
        <div className="bg-[#fbfbfb] min-h-screen">
            <div className="mx-auto w-[900px] max-w-full px-16 md:px-24 py-[64px]">
                <BackLink to="/" backText="Return to dashboard" />
                {sessionHistoryId && providerName && startDateTime ? (
                    <div className="mt-24 border border-strokedark bg-white py-16 md:py-32 px-16 md:px-[32px] rounded-r12">
                        <h1 className="text-lg font-semibold mb-24 capitalize">
                            Waiting room
                        </h1>
                        <div className="flex flex-col gap-y-24">
                            {displayNameFromUrl ? (
                                <div className="flex flex-col gap-y-4 text-sm">
                                    <span className="text-gray font-regular flex items-center gap-x-8">
                                        Display name
                                        <Button variant="normal" size="auto">
                                            <svg
                                                viewBox="0 0 24 24"
                                                width="24"
                                                height="24"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                fill="none"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="size-16 stroke-gray"
                                                onClick={() =>
                                                    setIsEditing(
                                                        (state) => !state
                                                    )
                                                }
                                            >
                                                <path d="M12 20h9" />
                                                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                                            </svg>
                                        </Button>
                                    </span>
                                    <span className="font-medium capitalize">
                                        {isEditing ? (
                                            <div className="inline-flex items-center gap-x-12">
                                                <Input
                                                    defaultValue={
                                                        displayNameFromUrl
                                                    }
                                                    ref={inputRef}
                                                    className="h-[32px] w-[200px]"
                                                />
                                                <div className="inline-flex items-center gap-x-8">
                                                    <Button
                                                        variant="normal"
                                                        size="auto"
                                                        onClick={() =>
                                                            setIsEditing(false)
                                                        }
                                                    >
                                                        <svg
                                                            viewBox="0 0 24 24"
                                                            width="24"
                                                            height="24"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            fill="none"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            className="size-16 stroke-danger"
                                                        >
                                                            <line
                                                                x1="18"
                                                                y1="6"
                                                                x2="6"
                                                                y2="18"
                                                            />
                                                            <line
                                                                x1="6"
                                                                y1="6"
                                                                x2="18"
                                                                y2="18"
                                                            />
                                                        </svg>
                                                    </Button>
                                                    <Button
                                                        variant="normal"
                                                        size="auto"
                                                        onClick={onSaveClick}
                                                    >
                                                        <svg
                                                            viewBox="0 0 24 24"
                                                            width="24"
                                                            height="24"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            fill="none"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            className="size-16 stroke-primary"
                                                        >
                                                            <polyline points="20 6 9 17 4 12" />
                                                        </svg>
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            displayNameFromUrl
                                        )}
                                    </span>
                                </div>
                            ) : null}
                            {providerName ? (
                                <div className="flex flex-col gap-y-4 text-sm">
                                    <span className="text-gray font-regular">
                                        Provider
                                    </span>
                                    <span className="font-medium capitalize">
                                        {providerName}
                                    </span>
                                </div>
                            ) : null}
                            <div className="flex flex-col gap-y-4 text-sm">
                                <span className="text-gray font-regular">
                                    Date
                                </span>
                                <span className="font-medium">
                                    {formatDate(startDateTime)}
                                </span>
                            </div>
                            <div className="flex flex-col gap-y-4 text-sm">
                                <span className="text-gray font-regular">
                                    Time
                                </span>
                                <span className="font-medium">
                                    {formatDate(
                                        startDateTime,
                                        false,
                                        "hh:mmaa"
                                    )}
                                </span>
                            </div>
                            <div className="flex flex-col gap-y-4 text-sm">
                                <span className="text-gray font-regular">
                                    Session Type
                                </span>
                                <span className="font-medium capitalize">
                                    {removeEnumUnderscore(service)}
                                </span>
                            </div>
                        </div>
                        {/* <div className="w-full p-8 rounded-r6 bg-[var(--color-orange-100)] text-sm text-[#6F510D] mt-16">
                        No one on call...
                    </div> */}
                        <div className="flex gap-x-16 items-center mt-32">
                            <Button
                                onClick={() =>
                                    navigate(
                                        `/meeting/${sessionHistoryId}/join?with=${providerName}&display_name=${displayNameFromUrl}`
                                    )
                                }
                            >
                                Join session
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={() => navigate(`/`)}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                ) : (
                    <ListState
                        isError={false}
                        stateHelperText="Please try to join the session again from your dashboard"
                        emptyMessage="Cannot load session at this time"
                        cardClass="h-[300px] mt-24"
                    />
                )}
            </div>
        </div>
    );
}
